import { useEffect, useMemo, useState } from 'react';
import { Modal, Table, Button, Popconfirm, Select, Typography, message, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useList } from '@refinedev/core';
import type { Conference } from '@viq-admin/types';
import {
  listConfigConferences,
  detachConfig,
  reapplyConfig,
  applyConfig,
} from '../../api/toolkitConfigs';
import { Can } from '../../ability/AbilityContext';
import { space } from '@viq-admin/ui';

/**
 * Modal listing every conference this reusable toolkit config is applied to,
 * with per-row detach and a "Reapply to all" action — PLUS (above that table)
 * the "Apply to conferences" section that actually applies the template to
 * new conferences. Before this, the modal only ever showed already-applied
 * conferences, so a fresh template (0 applications) rendered an empty popup
 * with no way to apply it at all.
 *
 * Backend reality (Foundation Task 7 `api/toolkitConfigs`):
 *  - `listConfigConferences(configId)` returns FULL `Conference[]` rows.
 *  - `reapplyConfig(configId)` returns `{ configId, count }` (NOT `{ updated }`).
 *  - `applyConfig(configId, conferenceId)` applies to ONE conference per call
 *    — the multi-select loops it once per selected id.
 *
 * Toolkit templates are global/cross-tenant, so the "all conferences" fetch
 * below passes `meta: { companyId: null }` to explicitly bypass the active
 * "operate as tenant" scope (dataProvider.getList treats an explicit
 * `companyId` key — including `null` — as an override; see dataProvider.ts).
 * Omitting the key would silently narrow the picker to whatever tenant the
 * operator currently happens to be scoped to.
 *
 * Mutating buttons (detach / reapply / apply) are gated on `manage Toolkit`;
 * a read-only super admin sees the conference list but no write controls.
 */
interface Props {
  configId: string;
  open: boolean;
  onClose: () => void;
}

export function AppliedConferencesModal({ configId, open, onClose }: Props) {
  const [rows, setRows] = useState<Conference[]>([]);
  const [loading, setLoading] = useState(false);
  const [reapplying, setReapplying] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [applying, setApplying] = useState(false);

  const { result: conferencesResult, query: conferencesQuery } = useList<Conference>({
    resource: 'conferences',
    // Force the cross-tenant union regardless of the active "operate as"
    // scope — see the file header comment for why.
    meta: { companyId: null },
  });

  const appliedIds = useMemo(() => new Set(rows.map((c) => c.id)), [rows]);
  const unappliedConferences = useMemo(
    () => (conferencesResult?.data ?? []).filter((c) => !appliedIds.has(c.id)),
    [conferencesResult, appliedIds],
  );

  function refreshApplied() {
    return listConfigConferences(configId).then(setRows);
  }

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listConfigConferences(configId)
      .then(setRows)
      .catch((err) =>
        message.error((err as { message?: string }).message ?? 'Failed to load'),
      )
      .finally(() => setLoading(false));
  }, [configId, open]);

  async function handleApply() {
    setApplying(true);
    try {
      const targetIds = selected;
      await Promise.all(targetIds.map((confId) => applyConfig(configId, confId)));
      await refreshApplied();
      setSelected([]);
      message.success(`Applied to ${targetIds.length} conference(s)`);
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Apply failed');
    } finally {
      setApplying(false);
    }
  }

  async function handleDetach(confId: string) {
    try {
      await detachConfig(configId, confId);
      setRows((prev) => prev.filter((c) => c.id !== confId));
      message.success('Detached');
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Detach failed');
    }
  }

  async function handleReapply() {
    setReapplying(true);
    try {
      const r = await reapplyConfig(configId);
      message.success(`Reapplied to ${r.count} conference(s)`);
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Reapply failed');
    } finally {
      setReapplying(false);
    }
  }

  const columns: ColumnsType<Conference> = [
    { dataIndex: 'name', key: 'name', title: 'Conference' },
    {
      key: 'actions',
      title: 'Actions',
      width: 100,
      render: (_, record) => (
        <Can I="manage" a="Toolkit" passThrough>
          {(allowed: boolean) =>
            allowed ? (
              <Popconfirm
                title="Detach this conference?"
                onConfirm={() => handleDetach(record.id)}
              >
                <Button type="link" danger size="small">
                  Detach
                </Button>
              </Popconfirm>
            ) : null
          }
        </Can>
      ),
    },
  ];

  return (
    <Modal
      title="Applied conferences"
      open={open}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Can key="reapply" I="manage" a="Toolkit" passThrough>
          {(allowed: boolean) =>
            allowed ? (
              <Popconfirm
                title="Push updates to all applied conferences?"
                onConfirm={handleReapply}
              >
                <Button type="primary" loading={reapplying}>
                  Reapply to all
                </Button>
              </Popconfirm>
            ) : null
          }
        </Can>,
      ]}
    >
      <Can I="manage" a="Toolkit" passThrough>
        {(allowed: boolean) =>
          allowed ? (
            <div style={{ marginBottom: space.xl }}>
              <Typography.Text strong>Apply to conferences</Typography.Text>
              <div style={{ display: 'flex', gap: space.sm, marginTop: space.sm }}>
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  style={{ flex: 1 }}
                  placeholder="Select conferences to apply this template to"
                  data-testid="apply-conferences-select"
                  value={selected}
                  onChange={setSelected}
                  loading={conferencesQuery?.isLoading}
                  options={unappliedConferences.map((c) => ({
                    label: c.name,
                    value: c.id,
                  }))}
                />
                <Button
                  type="primary"
                  onClick={handleApply}
                  loading={applying}
                  disabled={selected.length === 0}
                >
                  Apply
                </Button>
              </div>
            </div>
          ) : null
        }
      </Can>
      {loading ? (
        <Spin />
      ) : (
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="id"
          pagination={false}
        />
      )}
    </Modal>
  );
}
